/**
 * The pure half of `useUrlState` — how one filter write turns `prev` query params
 * into the next set.
 *
 * It lives apart from the hook so a plain-node guard can import and assert it
 * (`apps/web/scripts/check-url-state.mjs`); `useUrlState.ts` pulls in React and
 * react-router, which a guard script cannot load. Same split as `gqlError.ts`.
 */
export interface UrlWrite {
  /** The param being written. */
  key: string;
  /** Its default — writing this REMOVES the key, keeping the common URL clean. */
  def: string;
  /** The page-number param this write invalidates. Equal to `key` for page numbers. */
  pageKey: string;
  value: string;
}

export function nextSearchParams(prev: URLSearchParams, w: UrlWrite): URLSearchParams {
  const next = new URLSearchParams(prev);

  // Writing the value a param already holds is NOT a change, and must not clear the
  // page number.
  //
  // Device peers reset to page 1 about 300 ms after every load: its debounced search
  // committed `''` over `''` on mount, and that no-op write took `dpage` with it. Page
  // 2 of 4,199 devices was unreachable — the Next button set `?dpage=2` and the timer
  // stripped it right back off, and pasting the URL failed the same way.
  //
  // The guard belongs here rather than in the debounce because the contract this
  // restores is the one the line below already claims: a filter *change* invalidates
  // the page. A non-change never should have.
  if ((next.get(w.key) ?? w.def) === w.value) return next;

  if (w.value === w.def || w.value === '') next.delete(w.key);
  else next.set(w.key, w.value);

  // Any filter change invalidates the current page number.
  if (w.key !== w.pageKey) next.delete(w.pageKey);

  return next;
}
